import {
  ANCHOR_PROPERTY,
  anchorFromUrl,
  discountedPrice,
  tickedCompleteTheSetItems,
} from '@js/utils/completeTheSet';

/**
 * Complete the Set rows on the product page (blocks/rb-set-list.liquid).
 *
 * Liquid renders every row for the selected variant and gives each row its variant matches, keyed by the
 * main product's variant ID: `{ "<main variant ID>": [<row variant ID>, <price in cents>] }`. A main
 * variant with no entry means the row can't be matched, so it links to that product's page instead
 * ("Choose options"). This component only swaps the match when the shopper picks another variant.
 *
 * Ticked rows ride along with the product form's Add to cart (services/ActionHandler.js reads them). When
 * the product being viewed is already in the cart at full price, or the block is set to its own button,
 * "Add selected" adds the ticked rows on their own.
 */
export default {
  name: 'completeTheSetRows',
  component({ productId, selectedVariantId, addMode }) {
    // The block's element. Kept outside Alpine's reactive data, and used instead of $root, because rows
    // have their own x-data scope and $root inside them would be the row.
    let blockElement = null;

    return {
      productId: String(productId),
      selectedVariantId: String(selectedVariantId),
      anchorProductId: anchorFromUrl(productId) || String(productId),
      isOwnButton: addMode === 'own_button',
      tickedCount: 0,
      isAdding: false,
      errorMessage: '',

      init() {
        blockElement = this.$el;
        this.refreshTickedCount();

        window.addEventListener('variant-selected', (event) => {
          if (String(event.detail?.productId) !== this.productId || !event.detail?.variantId) return;
          this.selectedVariantId = String(event.detail.variantId);
          // A ticked row that can't be matched to the new variant becomes a link, so it no longer counts.
          this.$nextTick(() => this.refreshTickedCount());
        });

        // Untick rows once they are in the cart, so the next Add to cart doesn't add them again.
        window.addEventListener('cart:item_added', (event) => {
          const addedVariantIds = (event.detail?.items ?? [])
            .filter((item) => item.properties?.[ANCHOR_PROPERTY])
            .map((item) => String(item.id));
          if (addedVariantIds.length === 0) return;

          this.checkboxes().forEach((checkbox) => {
            if (addedVariantIds.includes(checkbox.value)) checkbox.checked = false;
          });
          this.refreshTickedCount();
        });
      },

      // [row variant ID, price] for the selected variant, or null when the row links instead
      matchFor(matches) {
        const match = matches?.[this.selectedVariantId];
        return match ? { variantId: String(match[0]), price: match[1] } : null;
      },

      discountedPriceFor(matches) {
        const match = this.matchFor(matches);
        return match ? discountedPrice(match.price) : 0;
      },

      hasSavingFor(matches) {
        const match = this.matchFor(matches);
        return match !== null && discountedPrice(match.price) < match.price;
      },

      // A page opened with ?complete_the_set_anchor= hides the anchor product's own row: it can't complete itself.
      isAnchorProduct(setProductId) {
        return String(setProductId) === this.anchorProductId;
      },

      urlFor(productUrl) {
        const url = new URL(productUrl, window.location.origin);
        url.searchParams.set('complete_the_set_anchor', this.anchorProductId);
        return `${url.pathname}${url.search}`;
      },

      // The product being viewed is in the cart on a normal, full-price line
      get isMainProductInCart() {
        return this.$store.cart.items.some(
          (item) => String(item.product_id) === this.productId && !item.properties?.[ANCHOR_PROPERTY],
        );
      },

      get isAddSelectedShown() {
        return this.isOwnButton || this.isMainProductInCart;
      },

      checkboxes() {
        return blockElement ? Array.from(blockElement.querySelectorAll('[data-complete-the-set-item]')) : [];
      },

      refreshTickedCount() {
        this.tickedCount = this.checkboxes().filter((checkbox) => checkbox.checked && !checkbox.disabled).length;
      },

      async addSelected() {
        if (!blockElement) return;
        const items = tickedCompleteTheSetItems(this.productId, this.anchorProductId, { root: blockElement });
        if (items.length === 0 || this.isAdding) return;

        this.isAdding = true;
        this.errorMessage = '';
        try {
          await this.$store.cart.addItems(items);
          window.dispatchEvent(new CustomEvent('cart:open'));
        } catch (error) {
          this.errorMessage = error.message;
        } finally {
          this.isAdding = false;
        }
      },
    };
  },
};
